import { create } from 'zustand';
import { useEffect, useRef } from 'react';
import { productosService, dtoAProducto } from '@/services/api/productos.service';
import type { Product, Category } from '@/modelos/productos/Producto';

export type ProductInput = Omit<Product, 'id' | 'fechaCreacion' | 'fechaActualizacion'>;

const PRODUCT_TTL_MS = 30 * 60 * 1000;

export interface CatalogStoreState {
  allProducts: Product[];
  onDemandProducts: Product[];
  categories: Category[];
  esCargando: boolean;
  _fetchedAt: number;
  fetchProducts: (force?: boolean) => Promise<void>;
  addProduct: (data: ProductInput) => Product;
  updateProduct: (id: string, data: Partial<Product>) => void;
  ensureProductById: (sku: string, productId: string) => void;
}

const fetchingProductSkus = new Set<string>();
let fetchInFlight = false;

const buildCategories = (productos: Product[]): Category[] => {
  const catMap = new Map<string, Category>();
  productos.forEach(p => {
    const nombre = (p.categoria ?? '').trim();
    if (!nombre) return;
    const key = nombre.toLowerCase();
    if (!catMap.has(key)) {
      catMap.set(key, { id: key, nombre, productCount: 0, fechaCreacion: p.fechaCreacion });
    }
    catMap.get(key)!.productCount += 1;
  });
  return Array.from(catMap.values());
};

const useProductStoreInternal = create<CatalogStoreState>((set, get) => ({
  allProducts: [],
  onDemandProducts: [],
  categories: [],
  esCargando: false,
  _fetchedAt: 0,

  fetchProducts: async (force = false) => {
    const state = get();
    const isStale = Date.now() - state._fetchedAt > PRODUCT_TTL_MS;
    if (!force && state._fetchedAt > 0 && !isStale) return;
    if (fetchInFlight) return;
    fetchInFlight = true;

    const esCargaInicial = state._fetchedAt === 0;
    if (esCargaInicial) set({ esCargando: true });
    try {
      const resp = await productosService.listar({ pageSize: 1000 });
      const productos: Product[] = Array.isArray(resp.data) ? resp.data.map(dtoAProducto) : [];
      fetchingProductSkus.clear();
      set({
        allProducts: productos,
        onDemandProducts: [],
        categories: buildCategories(productos),
        _fetchedAt: Date.now(),
      });
    } catch (error) {
      console.error('[useProductStore] Error al cargar productos:', error);
    } finally {
      fetchInFlight = false;
      if (esCargaInicial) set({ esCargando: false });
    }
  },

  addProduct: (data: ProductInput): Product => {
    const created: Product = {
      ...data,
      id: `local-${Date.now()}`,
      fechaCreacion: new Date(),
      fechaActualizacion: new Date(),
    };
    set(state => ({ allProducts: [created, ...state.allProducts] }));
    return created;
  },

  updateProduct: (id: string, data: Partial<Product>): void => {
    set(state => ({
      allProducts: state.allProducts.map(p =>
        p.id === id ? { ...p, ...data, fechaActualizacion: new Date() } : p
      ),
    }));
  },

  ensureProductById: (sku: string, productId: string): void => {
    if (!sku || !productId || productId.startsWith('local-') || productId.startsWith('libre-')) return;
    const state = get();
    if (state.allProducts.some(p => p.codigo === sku)) return;
    if (state.onDemandProducts.some(p => p.codigo === sku)) return;
    if (fetchingProductSkus.has(sku)) return;

    fetchingProductSkus.add(sku);
    void productosService.obtenerPorId(productId).then(res => {
      fetchingProductSkus.delete(sku);
      if (!res.ok || !res.data) return;
      const product = dtoAProducto(res.data);
      set(s => ({ onDemandProducts: [...s.onDemandProducts, product] }));
    });
  },
}));

export function useProductStore(opciones?: { lazy?: boolean }) {
  const store = useProductStoreInternal();
  const visibilityHandlerRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (opciones?.lazy) return;
    void store.fetchProducts();
  }, [opciones?.lazy]);

  useEffect(() => {
    visibilityHandlerRef.current = () => {
      if (!document.hidden && !opciones?.lazy) {
        void store.fetchProducts();
      }
    };
    document.addEventListener('visibilitychange', visibilityHandlerRef.current);
    return () => {
      if (visibilityHandlerRef.current) {
        document.removeEventListener('visibilitychange', visibilityHandlerRef.current);
      }
    };
  }, [opciones?.lazy]);

  return store;
}

useProductStore.getState = () => useProductStoreInternal.getState();
